import React, { useRef, useCallback, useContext, useState, useEffect } from 'react';
import { dummyCallback, EMPTY_ARRAY } from '../../../utils/complexDocumentConstants';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { PageElementsContext  } from '../../../context/pageElementsContext';
import { constructQuillJSON, constructAutoSaveJSON, convertContractToReact } from '../../../utils/TextBlockHelper';
import TextBlockWithQuillRenderer from '../TextBlockWithQuillRenderer';
import { useDefaultStyleContext } from '../../../context/complexDefaultStyleContext';
import { AccessibilitySeparatorTag } from '../ComplexBlockRenderer';

const DynamicTableColumnHeader = (props) => {
  const { uuid, headerContent, selected, scaling, onClick, editable, headerSelected, setSelectedEditor, relationships, isFirstRow,
     customClassName = '', cellSelected = false, dataSet = undefined, levels = EMPTY_ARRAY } = props;

  const { updateComplexElementDetails } = useContext(PageElementsContext);
  const [textEditable, setTextEditable] = useState(false);
  const containerRef = useRef(null);
  const fontChangeRef = useRef(null);

  const getDefaultStyleSelector = useCallback(store => ({fontFamily: store.fontFamily, fontSize:store.fontSize}), []);
  const [documentStyle] = useDefaultStyleContext(getDefaultStyleSelector);

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    `DynamicTableColumnHeader`
  );

  const documentStyleRef = useRef(documentStyle);

  const previewModeSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [pageOverflowEnabled] = useArtboardContext(previewModeSelector,  `DynamicTableColumnHeader-${uuid}`);

  const checkAccessibilitySelector = useCallback(store => store[CONTEXT_KEYS.CHECK_ACCESSIBILITY] , []);
  const [isAccessibility] = useArtboardContext(checkAccessibilitySelector, `BlockRenderer-${props.itemUUID}`);

  const saveBlock = useCallback((contents, undoDiff, redoDiff, cb, parentStyle = {}, op = undefined, newGlobalVariables) => {
    let isGroupHeader = false;
    let isRow = false;
    if(fontChangeRef.current){
      fontChangeRef.current=false;
      cb();
      return;
    };
    if (uuid.startsWith('gh-text-')) {
      isGroupHeader = true;
    }
    if (uuid.endsWith('row')) {
      isRow = true;
    }
    console.log("Saveblock :: ", contents, undoDiff, redoDiff, parentStyle);
    updateComplexElementDetails(uuid.replaceAll('gh-text-', '').replaceAll('-row', ''), {
      contents: constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, newGlobalVariables, false),
      isGroupHeader,
      isRow,
      style: { /*height: boundingRect?.height / scaling,*/ ...parentStyle },
      undoDiff,
      redoDiff,
      op,
      relationships
    }, 'table-header-value-with-style');
    cb();
  }, [documentStyle, scaling, uuid]);

  const onHeaderClick = useCallback((e) => {
    if (uuid.includes('-row-') && !isFirstRow) {
     onClick(e);
     return;
    }
    if (!selected) return;
    if (headerSelected) {
      return;
    }
    onClick(e);
  }, [onClick, selected, headerSelected, uuid, isFirstRow]);

  const onTextEditorDoubleClick = useCallback(() => {
    setSelectedEditor(uuid);
  }, [setSelectedEditor, uuid]);

  useEffect(() => {
    const handleFontStyleUpdate = e => {
      if(!editable) return;
      const { action, newFontFamilyOrSize, styleType } = e.detail;
      const eventData =
        action === 'update'
          ? {
              fontStyleData: newFontFamilyOrSize,
              styleType,
              action,
            }
          : { action };
      if (containerRef?.current) {
        fontChangeRef.current = true;
        documentStyleRef.current = Object.assign({}, documentStyleRef.current,{[styleType]:newFontFamilyOrSize});
        setTextEditable(true);
        setTimeout(() => {
          containerRef.current.dispatchEvent(
            new CustomEvent('updateFontFamilySizeEvent', { detail: eventData }),
          );
        }, 500);
      }
    };
    documentStyleRef.current = documentStyle;
    document.addEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    return () => {
      document.removeEventListener('textElementFontStyleUpdate', handleFontStyleUpdate);
    };
  }, []);

  return (
    <>
    <div
      id={uuid}
      onClick={onHeaderClick}
      className={customClassName}
      style={{
        height: headerContent.style.height || 'max-content',
        paddingTop: headerContent.style.padding.top,
        paddingLeft: headerContent.style.padding.left,
        paddingBottom: headerContent.style.padding.bottom,
        paddingRight: headerContent.style.padding.right
      }}
    >
      <TextBlockWithQuillRenderer
        ref={containerRef}
        defaultContent={constructQuillJSON(headerContent.contents, documentStyleRef.current?.fontFamily, documentStyleRef.current?.fontSize, globalVariables,
          pageOverflowEnabled, undefined, uuid.endsWith('row'))}
        saveBlock={saveBlock}
        isFromCreativeBlock={false}
        editable={editable}
        itemUUID={uuid}
        selected={selected && headerSelected}
        isPreview={pageOverflowEnabled}
        enableSideBar={dummyCallback}
        scaling={scaling}
        onContainerClick={onTextEditorDoubleClick}
        isFontSidebarAbsolute={true}
        textEditable={textEditable}
        setTextEditable={setTextEditable}
        // allowLocal={uuid.endsWith('row')}
        // onUpdateLocalVariables={dummyCallback}
        enableFunctionCall={true}
        relationships={relationships}
        levels={levels}
      >
        {pageOverflowEnabled || !textEditable ? convertContractToReact(headerContent?.contents, documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables, dataSet) : null}
      </TextBlockWithQuillRenderer>
      {isAccessibility && (
        <AccessibilitySeparatorTag
          accessibleInfo={{ accessibleType: 'dynamicTable', level: null }}
        />
      )}
    </div>
    {cellSelected && (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          background: '#00c4cc',
          opacity: 0.2
        }}
      />
    )}
    </>
  )
}

export default DynamicTableColumnHeader;